import { Controller } from "@hotwired/stimulus"

// Manages the Investigation Notebook panel — create/view/delete log investigations.
export default class extends Controller {
  static targets = ["panel", "trigger", "list", "nameInput", "statusMsg"]
  static values = { url: String }

  #open = false
  #boundOutside = null
  #investigations = []

  connect() {
    this.#boundOutside = e => {
      if (this.#open && !this.element.contains(e.target)) this.#close()
    }
    document.addEventListener("click", this.#boundOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.#boundOutside)
  }

  toggle() {
    this.#open ? this.#close() : this.#openPanel()
  }

  async createInvestigation(event) {
    event.preventDefault()
    const name = this.nameInputTarget.value.trim()
    if (!name) { this.nameInputTarget.focus(); return }

    const body = new URLSearchParams({
      "investigation[name]": name,
      authenticity_token: document.querySelector('meta[name="csrf-token"]')?.content || "",
    })

    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        body,
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      })
      if (resp.ok) {
        this.nameInputTarget.value = ""
        this.#showStatus("Investigation created!")
        await this.#loadList()
      } else {
        const data = await resp.json()
        this.#showStatus(data.errors?.join(", ") || "Failed to create")
      }
    } catch {
      this.#showStatus("Request failed")
    }
  }

  async deleteInvestigation(event) {
    const id = event.currentTarget.dataset.investigationId
    if (!id || !confirm("Delete this investigation?")) return

    const url = this.urlValue.replace(/\/$/, "") + `/${id}`
    try {
      await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      })
      await this.#loadList()
    } catch {
      this.#showStatus("Delete failed")
    }
  }

  async #openPanel() {
    this.#open = true
    this.panelTarget.classList.remove("hidden")
    this.triggerTarget.classList.add("active")
    await this.#loadList()
  }

  #close() {
    this.#open = false
    this.panelTarget.classList.add("hidden")
    this.triggerTarget.classList.remove("active")
  }

  async #loadList() {
    try {
      const resp = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      this.#investigations = await resp.json()
      this.#renderList()
    } catch {
      this.#showStatus("Failed to load investigations")
    }
  }

  #renderList() {
    if (!this.hasListTarget) return
    if (this.#investigations.length === 0) {
      this.listTarget.innerHTML = '<p class="siem-notebook-empty">No investigations yet.</p>'
      return
    }
    this.listTarget.innerHTML = this.#investigations.map(inv => `
      <div class="siem-notebook-item">
        <div class="siem-notebook-item-name">${this.#esc(inv.name)}</div>
        <div class="siem-notebook-item-meta">${inv.log_count} log${inv.log_count !== 1 ? "s" : ""}</div>
        <button class="btn btn-xs btn-danger siem-notebook-delete"
                data-action="click->accounts--logs--investigation#deleteInvestigation"
                data-investigation-id="${inv.id}"
                title="Delete investigation">
          <i class="fa fa-trash"></i>
        </button>
      </div>`).join("")
  }

  #showStatus(msg) {
    if (this.hasStatusMsgTarget) {
      this.statusMsgTarget.textContent = msg
      setTimeout(() => { if (this.hasStatusMsgTarget) this.statusMsgTarget.textContent = "" }, 3000)
    }
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
